package com.sddamico.components.widgets

import androidx.compose.runtime.Composable
import com.sddamico.SitePalette
import com.sddamico.models.TournamentRefResponse
import com.sddamico.util.abbreviateName
import com.sddamico.util.formatScheduleDate
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val GOLD = Color.rgb(0xC9A84C)
private val GOLD_BG = Color.rgb(0x3D3520)
private val GREEN = Color.rgb(0x4A8A7A)
private val GREEN_BG = Color.rgb(0x1A2F2A)
private val GRAY = Color.rgb(0x6A6A6A)

@Composable
fun ScheduleCard(tournament: TournamentRefResponse) {
    val isMajor = tournament.importance == "MAJOR"
    val borderColor = if (isMajor) GOLD else SitePalette.border

    Column(
        Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, borderColor)
            .borderRadius(6.px)
            .padding(topBottom = 0.375.cssRem, leftRight = 0.625.cssRem)
            .overflow(Overflow.Hidden)
    ) {
        // Row 1: Date + importance badge + status badge
        Row(
            Modifier.fillMaxWidth().gap(0.375.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                formatScheduleDate(tournament.startDate),
                Modifier
                    .fontSize(0.625.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(SitePalette.textMuted)
                    .styleModifier { property("letter-spacing", "0.03em") }
            )

            if (tournament.importance != "NORMAL") {
                ScheduleImportanceBadge(tournament.importance)
            }

            Spacer()

            ScheduleStatusBadge(tournament.status)
        }

        // Row 2: Tournament name
        SpanText(
            tournament.name,
            Modifier
                .fontSize(0.8125.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.text)
                .margin(top = 0.25.cssRem)
                .styleModifier {
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }
        )

        // Row 3: Winner/leader/location + Results link
        val hasResults = tournament.status == "COMPLETED" || tournament.status == "ACTIVE"
        val topPlayer = if (hasResults) {
            tournament.topResults?.firstOrNull { it.standing == 1 }
        } else null

        if (topPlayer != null && tournament.status == "COMPLETED") {
            Row(
                Modifier.fillMaxWidth().margin(top = 0.125.cssRem).gap(0.25.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText("\uD83C\uDFC6", Modifier.fontSize(0.6875.cssRem))
                SpanText(
                    abbreviateName(topPlayer.playerName),
                    Modifier
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .color(GOLD)
                )
                Spacer()
                ResultsLink(tournament.id)
            }
        } else if (topPlayer != null && tournament.status == "ACTIVE") {
            Row(
                Modifier.fillMaxWidth().margin(top = 0.125.cssRem).gap(0.25.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "1st",
                    Modifier
                        .fontSize(0.625.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(GOLD)
                )
                SpanText(
                    abbreviateName(topPlayer.playerName),
                    Modifier
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .color(SitePalette.textSecondary)
                )
                Spacer()
                ResultsLink(tournament.id)
            }
        } else if (tournament.location != null) {
            Row(
                Modifier.fillMaxWidth().margin(top = 0.125.cssRem).gap(0.25.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText("\uD83D\uDCCD", Modifier.fontSize(0.6875.cssRem))
                SpanText(
                    tournament.location,
                    Modifier
                        .fontSize(0.75.cssRem)
                        .color(SitePalette.textMuted)
                )
                if (hasResults) {
                    Spacer()
                    ResultsLink(tournament.id)
                }
            }
        } else if (hasResults) {
            Row(
                Modifier.fillMaxWidth().margin(top = 0.125.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Spacer()
                ResultsLink(tournament.id)
            }
        }
    }
}

@Composable
private fun ResultsLink(tournamentId: String) {
    val ctx = rememberPageContext()
    Div(
        Modifier
            .fontSize(0.6875.cssRem)
            .fontWeight(FontWeight.Medium)
            .color(SitePalette.primary)
            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
            .onClick {
                ctx.router.tryRoutingTo("/results#tournament-$tournamentId")
            }
            .styleModifier { property("white-space", "nowrap") }
            .toAttrs()
    ) {
        Text("Results \u2197")
    }
}

@Composable
private fun ScheduleImportanceBadge(importance: String) {
    val isMajor = importance == "MAJOR"
    Div(
        Modifier
            .borderRadius(9999.px)
            .padding(topBottom = 0.0625.cssRem, leftRight = 0.375.cssRem)
            .fontSize(0.5625.cssRem)
            .fontWeight(FontWeight.SemiBold)
            .color(GOLD)
            .run {
                if (isMajor) {
                    backgroundColor(Colors.Transparent)
                        .border(1.px, LineStyle.Solid, GOLD)
                } else {
                    backgroundColor(GOLD_BG)
                }
            }
            .styleModifier { property("letter-spacing", "0.04em") }
            .toAttrs()
    ) {
        Text(importance)
    }
}

@Composable
private fun ScheduleStatusBadge(status: String) {
    val (text, color, bg) = when (status) {
        "COMPLETED" -> Triple("Final", GRAY, Color.rgb(0x1F1F1F))
        "ACTIVE" -> Triple("Live", GREEN, GREEN_BG)
        else -> Triple("Soon", SitePalette.textMuted, Colors.Transparent)
    }

    if (status == "FUTURE") return // Don't show badge for future tournaments far out

    Div(
        Modifier
            .borderRadius(9999.px)
            .padding(topBottom = 0.0625.cssRem, leftRight = 0.375.cssRem)
            .fontSize(0.5625.cssRem)
            .fontWeight(FontWeight.Medium)
            .color(color)
            .backgroundColor(bg)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
fun EditableTournamentCard(
    tournament: TournamentRefResponse,
    isScored: Boolean,
    importance: String,
    onScoredChange: (Boolean) -> Unit,
    onImportanceChange: (String) -> Unit,
) {
    val isMajor = importance == "MAJOR"
    val borderColor = if (isMajor) GOLD else SitePalette.border

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, borderColor)
            .borderRadius(6.px)
            .overflow(Overflow.Hidden)
    ) {
        // Top section: tournament info (dimmed when unscored)
        Column(
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 0.375.cssRem, leftRight = 0.625.cssRem)
                .styleModifier { property("opacity", if (isScored) "1" else "0.5") }
        ) {
            // Row 1: Date + importance badge
            Row(
                Modifier.fillMaxWidth().gap(0.375.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    formatScheduleDate(tournament.startDate),
                    Modifier
                        .fontSize(0.625.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(SitePalette.textMuted)
                        .styleModifier { property("letter-spacing", "0.03em") }
                )

                if (importance != "NORMAL") {
                    ScheduleImportanceBadge(importance)
                }
            }

            // Row 2: Tournament name
            SpanText(
                tournament.name,
                Modifier
                    .fontSize(0.8125.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .color(SitePalette.text)
                    .margin(top = 0.25.cssRem)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )

            // Row 3: Location
            if (tournament.location != null) {
                Row(
                    Modifier.fillMaxWidth().margin(top = 0.125.cssRem).gap(0.25.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText("\uD83D\uDCCD", Modifier.fontSize(0.6875.cssRem))
                    SpanText(
                        tournament.location,
                        Modifier
                            .fontSize(0.75.cssRem)
                            .color(SitePalette.textMuted)
                    )
                }
            }

            // Row 4: Purse
            val purseDisplay = tournament.purse?.let { formatPurse(it) }
            if (purseDisplay != null) {
                Row(
                    Modifier.fillMaxWidth().margin(top = 0.125.cssRem).gap(0.25.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText("\uD83D\uDCB0", Modifier.fontSize(0.6875.cssRem))
                    SpanText(
                        purseDisplay,
                        Modifier
                            .fontSize(0.75.cssRem)
                            .color(SitePalette.textMuted)
                    )
                }
            }
}

        // Divider
        Div(
            Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(SitePalette.border)
                .toAttrs()
        )

        // Bottom row: Scored toggle + Importance selector
        Row(
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 0.5.cssRem, leftRight = 0.625.cssRem)
                .gap(1.5.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Scored toggle
            Row(
                Modifier.gap(0.5.cssRem).cursor(Cursor.Pointer).onClick { onScoredChange(!isScored) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "Scored",
                    Modifier.fontSize(0.75.cssRem).fontWeight(FontWeight.Medium).color(SitePalette.textSecondary)
                )
                TogglePill(isScored)
            }

            Spacer()

            // Importance selector
            Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "Type",
                    Modifier.fontSize(0.75.cssRem).fontWeight(FontWeight.Medium).color(SitePalette.textSecondary)
                )
                Select(attrs = {
                    addEventListener("change") { onImportanceChange(it.target.asDynamic().value as String) }
                    style {
                        padding(4.px, 8.px)
                        fontSize(0.75.cssRem)
                        border(1.px, LineStyle.Solid, SitePalette.border)
                        borderRadius(6.px)
                        backgroundColor(SitePalette.background)
                        color(SitePalette.text)
                        property("outline", "none")
                    }
                }) {
                    listOf("NORMAL", "SPECIAL", "MAJOR").forEach { value ->
                        Option(value, attrs = { if (importance == value) selected() }) {
                            Text(value.lowercase().replaceFirstChar { it.uppercase() })
                        }
                    }
                }
            }
        }
    }
}

/** Formats a purse amount in cents to a display string like "$25,000,000" */
private fun formatPurse(cents: Long): String? {
    if (cents <= 0) return null
    val dollars = cents / 100
    val formatted = dollars.toString().reversed().chunked(3).joinToString(",").reversed()
    return "$$formatted"
}
